import { ChangeDetectionStrategy, Component, DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DataNoRowOverlayComponent } from '@examdojo/ag-grid';
import { getAnimationAppearOnTrigger } from '@examdojo/animation';
import { SyllabusQuery } from '@examdojo/category/v2';
import { DateTimeFormat, DateTimeService } from '@examdojo/core/date-time';
import { MasterListViewComponent, provideSimpleListView } from '@examdojo/core/list-view';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { QUESTION_STATUS_TO_LABEL, QUESTION_STATUSES, QuestionStatus } from '@examdojo/models/question';
import { DerivativeQuestionItemsViewStoreModel, QuestionEntityService } from '@examdojo/question';
import { DerivativeQuestionHistoryDialogService } from '@examdojo/review';
import { SOURCE_STATUS_LIST, SOURCE_STATUS_TO_LABEL, SourceListItemHttpModel, SourceStatus } from '@examdojo/source';
import { LoaderComponent } from '@examdojo/ui/loader';
import {
  ColDef,
  FilterModel,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  RowClickedEvent,
  SortModelItem,
  ValueFormatterParams,
} from 'ag-grid-community';
import { finalize, first, map, of, tap } from 'rxjs';
import { CmsUrlParts, NEW_QUESTION_ID_PARAM } from '../../cms/cms.model';
import { AgGirdColumnStateManager } from '../../shared/ag-grid/ag-gird-column-state.manager';
import { QuestionListService } from '../services/question-list.service';
import { QuestionListStatusCellRendererComponent } from './question-list-status-cell-renderer.component';

const QUESTION_LIST_VIEW_STATE_STORAGE_KEY = 'question-list-view-state';

@Component({
  selector: 'exam-question-list',
  templateUrl: './question-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MasterListViewComponent, LoaderComponent],
  providers: [provideSimpleListView(QuestionListService)],
  animations: [getAnimationAppearOnTrigger()],
})
export class QuestionListComponent {
  constructor(
    private readonly dateTimeService: DateTimeService,
    private readonly router: Router,
    private readonly questionService: QuestionEntityService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly destroyRef: DestroyRef,
    private readonly syllabusQuery: SyllabusQuery,
    private readonly derivativeQuestionHistoryDialogService: DerivativeQuestionHistoryDialogService,
  ) {
    this.syllabusQuery.active$
      .pipe(
        tap(() => this.gridApi()?.refreshServerSide()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
  readonly gridApi = signal<GridApi<DerivativeQuestionItemsViewStoreModel> | null>(null);

  readonly isLoading = signal<boolean>(true);

  readonly columnDefs: Array<ColDef<DerivativeQuestionItemsViewStoreModel>> = [
    {
      field: 'id',
      headerName: 'ID',
      valueGetter: ({ data }) => {
        return data?.id ?? '';
      },
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['equals'],
        maxNumConditions: 1,
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      minWidth: 150,
      cellRenderer: QuestionListStatusCellRendererComponent,
      filterParams: {
        values: QUESTION_STATUSES,
        valueFormatter: (params: ValueFormatterParams<DerivativeQuestionItemsViewStoreModel, string>) =>
          QUESTION_STATUS_TO_LABEL[params.value as QuestionStatus],
      },
    },
    {
      field: 'type',
      headerName: 'Type',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      },
      width: 100,
    },
    {
      headerName: 'Updated At',
      field: 'updated_at',
      valueFormatter: ({ data }) => {
        if (!data || !data.updated_at) {
          return '';
        }
        return this.dateTimeService.formatDateRelativeToNow(data.updated_at, true);
      },
      tooltipValueGetter: ({ data }) => {
        if (!data || !data.updated_at) {
          return '';
        }
        return this.dateTimeService.formatDate(data.updated_at, DateTimeFormat.ShortDateTime);
      },
    },
    {
      field: 'source_name',
      headerName: 'Source Name',
      valueGetter: ({ data }) => {
        return data?.source_name ?? '';
      },
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      },
    },
    {
      field: 'source_status',
      headerName: 'Source Status',
      valueGetter: ({ data }) => {
        if (!data?.source_status) {
          return '';
        }
        return SOURCE_STATUS_TO_LABEL[data.source_status as SourceStatus];
      },
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      filterParams: {
        values: SOURCE_STATUS_LIST,
        valueFormatter: (params: ValueFormatterParams<SourceListItemHttpModel, SourceStatus>) =>
          SOURCE_STATUS_TO_LABEL[params.value as SourceStatus],
      },
    },
    {
      field: 'topics',
      headerName: 'Topics',
      valueGetter: ({ data }) => {
        return data?.topics?.map((topic) => topic.code).join(', ') ?? '';
      },
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      },
    },
    {
      field: 'total_marks',
      headerName: 'Total Marks',
      valueGetter: ({ data }) => {
        return data?.total_marks ?? '';
      },
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['equals'],
        maxNumConditions: 1,
      },
    },
    {
      field: 'total_stems',
      headerName: 'Total Stem',
      valueGetter: ({ data }) => {
        return data?.total_stems ?? '';
      },
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['equals'],
        maxNumConditions: 1,
      },
    },
  ];

  readonly serverSideDatasource: IServerSideDatasource = {
    getRows: (params: IServerSideGetRowsParams) => {
      const { startRow, endRow, sortModel, filterModel } = params.request;
      const sortBy: SortModelItem[] = sortModel.length ? sortModel : [{ colId: 'updated_at', sort: 'desc' }];

      this.questionService
        .fetchPaginatedListView({
          startRow,
          endRow: endRow ? endRow - 1 : undefined,
          sortBy,
          filterBy: {
            ...filterModel,
            syllabus_id: {
              filterType: 'set',
              values: [this.syllabusQuery.getActiveId()!],
            },
          } as FilterModel,
        })
        .pipe(
          first(),
          map(({ data, totalCount }) => ({
            data,
            totalCount,
          })),
          tap({
            next: (data) => {
              params.success({ rowData: data.data, rowCount: data.totalCount });
              this.columnStateManager.initialRowsLoaded.set(true);
            },
            error: () => {
              params.fail();
            },
          }),
          this.errorHandlerService.catchError(
            `[QuestionListComponent.getRows]: failed to load the questions`,
            () => of(null),
            { toast: `Could not load the questions` },
          ),
          finalize(() => this.isLoading.set(false)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    },
  };

  readonly gridOptions: GridOptions<DerivativeQuestionItemsViewStoreModel> = {
    pagination: true,
    paginationAutoPageSize: true,
    cacheBlockSize: 100,
    rowModelType: 'serverSide',
    serverSideDatasource: this.serverSideDatasource,
    suppressCellFocus: true,
    noRowsOverlayComponent: DataNoRowOverlayComponent,
    noRowsOverlayComponentParams: {
      text: 'No questions found',
      body: 'Please try with another filter',
    },
    defaultColDef: {
      sortable: true,
      resizable: true,
      suppressMovable: true,
      menuTabs: [],
    },
    context: {
      componentParent: this,
    },
    animateRows: true,
    rowSelection: 'single',
    getContextMenuItems: (event) => [
      {
        name: 'Open in New Tab',
        icon: `<i class="fa-light fa-arrow-up-right-from-square"></i>`,
        action: () => {
          if (!event.node?.data?.id) {
            return;
          }

          const activeSyllabus = this.syllabusQuery.getActive();
          if (!activeSyllabus) {
            return;
          }

          window.open(
            `/${CmsUrlParts.Syllabus}/${activeSyllabus.slug}/${CmsUrlParts.Questions}/${event.node.data.id}`,
            '_blank',
            'noopener,noreferrer',
          );
        },
      },
      {
        name: 'Open Source in New Tab',
        icon: `<i class="fa-light fa-arrow-up-right-from-square"></i>`,
        action: () => {
          if (!event.node?.data?.source_id) {
            return;
          }

          const activeSyllabus = this.syllabusQuery.getActive();
          if (!activeSyllabus) {
            return;
          }

          window.open(
            `/${CmsUrlParts.Syllabus}/${activeSyllabus.slug}/${CmsUrlParts.Sources}/${event.node.data.source_id}`,
            '_blank',
            'noopener,noreferrer',
          );
        },
      },
      {
        name: 'Open Reference Question in New Tab',
        icon: `<i class="fa-light fa-arrow-up-right-from-square"></i>`,
        action: () => {
          if (!event.node?.data?.reference_question_id) {
            return;
          }

          const activeSyllabus = this.syllabusQuery.getActive();
          if (!activeSyllabus) {
            return;
          }

          window.open(
            `/${CmsUrlParts.Syllabus}/${activeSyllabus.slug}/${CmsUrlParts.ReferenceQuestions}/${event.node.data.reference_question_id}`,
            '_blank',
            'noopener,noreferrer',
          );
        },
      },
      {
        name: 'View History',
        icon: `<i class="fa-light fa-timeline"></i>`,
        action: () => {
          if (!event.node?.data?.id) {
            return;
          }

          this.openQuestionHistoryDialog(event.node.data.id);
        },
      },
    ],
    rowClass: 'cursor-pointer',
    onPaginationChanged: () => {
      this.columnStateManager.saveGridState();
    },
    onSortChanged: () => {
      this.columnStateManager.saveGridState();
    },
    onFilterChanged: () => {
      this.columnStateManager.saveGridState();
    },
  };

  readonly columnStateManager = new AgGirdColumnStateManager(this.gridOptions, QUESTION_LIST_VIEW_STATE_STORAGE_KEY);

  addQuestion() {
    const activeSyllabus = this.syllabusQuery.getActive();
    if (!activeSyllabus) {
      return;
    }

    this.router.navigate([
      '/',
      CmsUrlParts.Syllabus,
      activeSyllabus.slug,
      CmsUrlParts.Questions,
      NEW_QUESTION_ID_PARAM,
    ]);
  }

  openQuestion(event: RowClickedEvent<DerivativeQuestionItemsViewStoreModel, void>) {
    if (!event.data) {
      return;
    }

    const activeSyllabus = this.syllabusQuery.getActive();
    if (!activeSyllabus) {
      return;
    }

    this.router.navigate(['/', CmsUrlParts.Syllabus, activeSyllabus.slug, CmsUrlParts.Questions, event.data.id]);
  }

  async openQuestionHistoryDialog(questionId: number) {
    await this.derivativeQuestionHistoryDialogService.openDialog({
      input: { questionId },
    });
  }

  onGridReady(evt: GridReadyEvent) {
    this.gridApi.set(evt.api);

    this.columnStateManager.gridApi.set(evt.api);
    this.columnStateManager.gridReady.set(true);
    this.columnStateManager.loadGridState();
  }
}
